import React, { useEffect, useState, useCallback } from 'react';
import { AWSCredentials } from '../services/cognitoService';
import { fetchContent, saveContent } from '../services/s3Service';
import { Message } from '../types';
import MessageDisplay from './MessageDisplay';
import styles from './ContentEditor.module.css';

interface ContentEditorProps {
  credentials: AWSCredentials;
}

export default function ContentEditor({ credentials }: ContentEditorProps) {
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);

  const loadContent = useCallback(async () => {
    setLoading(true);
    try {
      const text = await fetchContent(credentials);
      setContent(text);
      setOriginalContent(text);
      setHasChanges(false);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to load content',
      });
    } finally {
      setLoading(false);
    }
  }, [credentials]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setContent(newContent);
    setHasChanges(newContent !== originalContent);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveContent(credentials, content);
      setOriginalContent(content);
      setHasChanges(false);
      setMessage({
        type: 'success',
        text: 'Content saved successfully',
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to save content',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setContent(originalContent);
    setHasChanges(false);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>Loading content...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.editorWrapper}>
        <h2 className={styles.subtitle}>Event Information</h2>
        <textarea
          value={content}
          onChange={handleContentChange}
          disabled={saving}
          className={styles.textarea}
          placeholder="Enter event information here..."
        />

        <div className={styles.buttonGroup}>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={styles.saveButton}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            onClick={handleReset}
            disabled={!hasChanges || saving}
            className={styles.resetButton}
          >
            Reset
          </button>
        </div>

        {message && (
          <MessageDisplay
            message={message}
            onDismiss={() => setMessage(null)}
          />
        )}
      </div>

      <div className={styles.previewWrapper}>
        <h2 className={styles.subtitle}>Preview</h2>
        <div className={styles.preview}>
          <div
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>
      </div>
    </div>
  );
}
